import React, { FC } from "react";

import { DataViewKey } from "@keepeek/commons";
import { BaseComponentProps, ComponentOverrideKey } from "@keepeek/refront-components";
import { Box } from "@mui/material";
import clsx from "clsx";
import { useRecoilValue } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { getCustomerOverrideComponent } from "../../../lib/overrides";
import { Template } from "../../../models/configuration/definitions/template";
import { Theme } from "../../../models/configuration/theme";
import { KTemplatesTemplatesName } from "../../../providers/config/utils";
import { frontEditState } from "../../../providers/frontEdit/atoms/frontEdit";
import FrontEditKTemplates from "../../FrontEdit/FrontEditKTemplates";
import { usePreloadWidgets } from "../hooks";
import { WidgetConfigurations } from "../models";
import KTemplateWrapper from "./KTemplatesWrapper";

export type KTemplatesProps = {
  templatesLocation?: {
    templatesPage: CONFIGURATION_SECTION_PATH;
    templatesName: KTemplatesTemplatesName;
  };
  templatesConfiguration: Template[];
  widgetsConfiguration: WidgetConfigurations | null;
  /**
   * Theme and assets configuration.
   * Do not remove, it's useful for refront-customers
   */
  themeConfiguration: Theme | null;
  dataViewKey?: DataViewKey;
} & BaseComponentProps;

const KTemplates: FC<React.PropsWithChildren<KTemplatesProps>> = function ({
  templatesLocation,
  templatesConfiguration,
  widgetsConfiguration,
  className,
  style,
  dataViewKey = DataViewKey.SEARCH,
}) {
  const {
    templatesConfiguration: templatesConfigurationPreloaded,
    widgetsConfiguration: widgetsConfigurationPreloaded,
  } = usePreloadWidgets({
    templatesConfiguration,
    widgetsConfiguration,
  });

  const frontEdit = useRecoilValue(frontEditState);

  if (
    !widgetsConfigurationPreloaded ||
    !templatesConfigurationPreloaded ||
    templatesConfigurationPreloaded.length === 0
  ) {
    return null;
  }

  if (frontEdit && templatesLocation && templatesConfigurationPreloaded.length > 1) {
    return (
      <FrontEditKTemplates
        templatesLocation={templatesLocation}
        templatesConfigurationPreloaded={templatesConfigurationPreloaded}
        widgetsConfigurationPreloaded={widgetsConfigurationPreloaded}
        className={className}
        style={style}
        dataViewKey={dataViewKey}
      />
    );
  }

  return (
    <Box
      className={clsx("KTemplates", className)}
      style={style}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {templatesConfigurationPreloaded.map((template, indexT) => {
        return (
          <KTemplateWrapper
            key={indexT}
            classNameIndex={indexT}
            template={template}
            widgetsConfigurationPreloaded={widgetsConfigurationPreloaded}
            dataViewKey={dataViewKey}
          />
        );
      })}
    </Box>
  );
};

export default getCustomerOverrideComponent<KTemplatesProps>(
  ComponentOverrideKey.KTemplates,
  KTemplates,
);
